/**
 * The API reference tab of Integrations: every operation an API key may call, drawn from the integration OpenAPI
 * document the server cuts from its own contract (``GET /api/integration/openapi.json``), so it cannot drift from the
 * API. Each operation shows its method, path, summary, description, parameters, body fields and answers, with the
 * app's own components instead of a third-party viewer: no script from elsewhere, the design tokens, light and dark.
 */

import { BookOpenText, Download } from 'lucide-react';

import { type IntegrationOperation, type JsonSchema, useIntegrationOpenapi } from '@/api/queries';
import { LoadFailure, LoadingCard, StateCard } from '@/ui/StateCard';

type Schemas = Record<string, JsonSchema>;

/** A method's badge tone: reads calm, creates and changes warmer, a delete in danger. */
const METHOD_TONE: Record<string, string> = {
  get: 'badge-info',
  post: 'badge-success',
  put: 'badge-warning',
  patch: 'badge-warning',
  delete: 'badge-danger',
};

/** A schema's name or type, as a reader wants it: `AssetOut`, `string (uuid)`, `list of string`, `integer or null`. */
export function typeName(schema: JsonSchema | undefined): string {
  if (!schema) return 'any';
  if (schema.$ref) return schema.$ref.split('/').pop() ?? 'object';
  if (schema.anyOf) return schema.anyOf.map(typeName).join(' or ');
  if (schema.enum) return schema.enum.map(String).join(' · ');
  if (schema.type === 'array') return `list of ${typeName(schema.items)}`;
  const type = Array.isArray(schema.type) ? schema.type.join(' or ') : (schema.type ?? 'object');
  return schema.format ? `${type} (${schema.format})` : type;
}

function resolve(schema: JsonSchema | undefined, schemas: Schemas): JsonSchema | undefined {
  return schema?.$ref ? schemas[schema.$ref.split('/').pop() ?? ''] : schema;
}

function Fields({ schema, schemas }: { schema: JsonSchema | undefined; schemas: Schemas }) {
  const object = resolve(schema, schemas);
  if (!object?.properties) return <p className="hint">{typeName(schema)}</p>;
  const required = new Set(object.required ?? []);
  return (
    <div className="table-scroll">
      <table className="data compact">
        <thead>
          <tr>
            <th scope="col">Field</th>
            <th scope="col">Type</th>
            <th scope="col">Required</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(object.properties).map(([name, field]) => (
            <tr key={name}>
              <th scope="row" className="mono">{name}</th>
              <td>
                {typeName(field)}
                {field.maxLength ? <span className="hint"> · up to {field.maxLength}</span> : null}
              </td>
              <td>{required.has(name) ? 'Yes' : 'No'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Parameters({ op }: { op: IntegrationOperation }) {
  if (!op.parameters?.length) return null;
  return (
    <div className="table-scroll">
      <table className="data compact">
        <thead>
          <tr>
            <th scope="col">Parameter</th>
            <th scope="col">In</th>
            <th scope="col">Type</th>
            <th scope="col">Required</th>
          </tr>
        </thead>
        <tbody>
          {op.parameters.map((p) => (
            <tr key={`${p.in}-${p.name}`}>
              <th scope="row" className="mono">{p.name}</th>
              <td>{p.in}</td>
              <td>{typeName(p.schema)}</td>
              <td>{p.required ? 'Yes' : 'No'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Operation({ method, path, op, schemas }: { method: string; path: string; op: IntegrationOperation; schemas: Schemas }) {
  const body = op.requestBody?.content['application/json']?.schema;
  const ok = Object.entries(op.responses).find(([code]) => code.startsWith('2'));
  const errors = Object.entries(op.responses).filter(([code]) => !code.startsWith('2'));
  return (
    <article className="stack-sm" aria-labelledby={op.operationId}>
      <h3 id={op.operationId} className="config-subhead">
        <span className={`badge ${METHOD_TONE[method] ?? 'badge-neutral'} mono`}>{method.toUpperCase()}</span> <span className="mono">{path}</span>
      </h3>
      <p>
        <strong>{op.summary}.</strong> {op.description}
      </p>
      <Parameters op={op} />
      {body && (
        <>
          <p>Body: {typeName(body)}</p>
          <Fields schema={body} schemas={schemas} />
        </>
      )}
      {ok && <p>Answers {ok[0]} with {typeName(ok[1].content?.['application/json']?.schema)}.</p>}
      <ul aria-label={`Errors of ${op.summary}`}>
        {errors.map(([code, answer]) => (
          <li key={code}>
            <span className="mono">{code}</span>: {answer.description}
          </li>
        ))}
      </ul>
      <p className="mono">operationId: {op.operationId}</p>
    </article>
  );
}

export function ApiReferenceTab() {
  const doc = useIntegrationOpenapi();
  if (doc.isPending) return <LoadingCard label="Loading the API reference" />;
  if (doc.error) return <LoadFailure error={doc.error} onRetry={() => void doc.refetch()} />;
  const operations = Object.entries(doc.data.paths).flatMap(([path, ops]) => Object.entries(ops).map(([method, op]) => ({ path, method, op })));
  if (operations.length === 0) return <StateCard icon={<BookOpenText aria-hidden="true" />} title="No operations are open to API keys." />;
  return (
    <div className="stack-md">
      <div className="toolbar">
        <p className="hint">
          {doc.data.info.title}, version {doc.data.info.version}: {operations.length} operations. {doc.data.info.description}
        </p>
        <a className="btn btn-secondary" href="/api/integration/openapi.json" download="wynxx-integration-openapi.json">
          <Download aria-hidden="true" /> OpenAPI document
        </a>
      </div>
      <h2 className="config-subhead">Operations</h2>
      {operations.map(({ path, method, op }) => (
        <Operation key={op.operationId} method={method} path={path} op={op} schemas={doc.data.components.schemas} />
      ))}
    </div>
  );
}
